// 📄 components/StepPOTest.tsx
import React, { useState } from 'react';
import { Play, RefreshCw, CheckCircle2, XCircle } from 'lucide-react';
import { PurchaseOrderFormData } from '../../../../../services/commerce/integrationService';

interface StepPOTestProps {
  testPoNumber: string;
  transmissionStatus: string;
  onChange: (field: keyof PurchaseOrderFormData, value: any) => void;
  isInvalid: boolean;
}

export function StepPOTest({ testPoNumber, transmissionStatus, onChange, isInvalid }: StepPOTestProps) {
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<'idle' | 'success' | 'error'>('idle');

  const runTestSimulation = () => {
    setTesting(true);
    setTestResult('idle');
    setTimeout(() => {
      setTesting(false);
      if (testPoNumber && testPoNumber.trim().length >= 8) {
        setTestResult('success');
        onChange('transmissionStatus', 'Exitoso');
      } else {
        setTestResult('error');
        onChange('transmissionStatus', 'Fallido');
      }
    }, 2000);
  };

  return (
    <div className="space-y-4 animate-fadeIn">
      {/* SPRO Route */}
      <div className="bg-blue-500/5 border border-blue-500/10 rounded-xl p-3 mb-2 space-y-1">
        <span className="text-[10px] font-bold text-blue-400 uppercase tracking-widest block">Transacción SAP de Transmisión</span>
        <p className="font-mono text-xs text-gray-300">
          <span className="bg-zinc-800 px-1.5 py-0.5 rounded text-gray-400 font-bold mr-1.5">ME9F / ME21N</span> 
          <span>SAP Menu → Logistics → Materials Management → Purchasing → Purchase Order → Messages → Print/Transmit</span>
        </p>
      </div>

      <div>
        <h3 className="text-lg font-medium text-blue-400">5. Pruebas End-to-End y Validación</h3>
        <p className="text-sm text-gray-400">
          Envía una orden de compra piloto desde SAP (ej: mediante la creación con <code className="bg-black/30 px-1 rounded">ME21N</code> y envío con <code className="bg-black/30 px-1 rounded">ME9F</code>) y registra el ID de documento para validar la conexión de extremo a extremo.
        </p>
      </div>

      <div className="bg-white/5 p-4 rounded-xl border border-white/5 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-medium text-gray-400 mb-1">
            Número de Orden de Compra de Prueba <span className="text-red-400">*</span>
          </label>
          <input
            type="text"
            value={testPoNumber}
            onChange={(e) => onChange('testPoNumber', e.target.value.replace(/\D/g, ''))}
            placeholder="ej. 4500012345"
            maxLength={10}
            className={`w-full bg-zinc-800 border rounded-lg p-2.5 text-sm font-mono text-white focus:outline-none focus:border-blue-500 ${
              isInvalid && !testPoNumber ? 'border-red-500' : 'border-white/10'
            }`}
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-400 mb-1">Estado de la Transmisión</label>
          <select
            value={transmissionStatus}
            onChange={(e) => onChange('transmissionStatus', e.target.value)}
            className="w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm text-white focus:outline-none focus:border-blue-500"
          >
            <option value="Pendiente">Pendiente (No enviado)</option>
            <option value="Exitoso">Exitoso (Recibido en Ariba)</option>
            <option value="Fallido">Fallido / Con errores</option>
          </select>
        </div>
      </div>

      {/* Simulador de transmisión de prueba */}
      <div className="bg-zinc-900/40 border border-white/5 rounded-xl p-4 flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="space-y-1 text-center sm:text-left">
          <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider block">Simular Conectividad ALE / CIG</span>
          <p className="text-xs text-gray-400">
            Valida si el puerto de salida y el proyecto CIG están configurados simulando el despacho del IDoc.
          </p>
        </div>
        
        <button
          type="button"
          onClick={runTestSimulation}
          disabled={testing}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-[#fff] font-medium text-xs px-4 py-2.5 rounded-lg shadow-md transition-all disabled:bg-gray-600"
        >
          {testing ? (
            <>
              <RefreshCw className="w-3.5 h-3.5 animate-spin" />
              Simulando envío...
            </>
          ) : (
            <>
              <Play className="w-3.5 h-3.5" />
              Probar Transmisión
            </>
          )}
        </button>
      </div>

      {/* Resultados de la Simulación */}
      {testResult === 'success' && (
        <div className="bg-green-500/10 border border-green-500/20 text-green-400 p-4 rounded-xl flex items-center gap-3 text-sm animate-fadeIn">
          <CheckCircle2 className="w-5 h-5 flex-shrink-0" />
          <div>
            <span className="font-semibold block">¡Conexión Exitosa!</span>
            <span className="text-xs text-green-500/80">La orden de compra piloto {testPoNumber} se ha transmitido correctamente a SAP Ariba.</span>
          </div>
        </div>
      )}

      {testResult === 'error' && (
        <div className="bg-red-500/10 border border-red-500/20 text-red-400 p-4 rounded-xl flex items-center gap-3 text-sm animate-fadeIn">
          <XCircle className="w-5 h-5 flex-shrink-0" />
          <div>
            <span className="font-semibold block">Fallo en la Simulación</span>
            <span className="text-xs text-red-500/80">Por favor ingrese un número de Orden de Compra válido (ej: 4500000001) para completar el test de salida.</span>
          </div>
        </div>
      )}
    </div>
  );
}
